import React, { useState } from 'react';
import { Users, LogOut, Package, Camera, FileText, FileSignature, Megaphone } from 'lucide-react';
import ContactsTab from '../components/admin/ContactsTab';
import ProductsTab from '../components/admin/ProductsTab';
import RealizationsTab from '../components/admin/RealizationsTab';
import CataloguePdfTab from '../components/admin/CataloguePdfTab';
import QuotesTab from '../components/admin/QuotesTab';
import PromoTab from '../components/admin/PromoTab';

const API = process.env.REACT_APP_BACKEND_URL;
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD;

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('contacts');
  const [contacts, setContacts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [realizations, setRealizations] = useState([]);
  const [showRealForm, setShowRealForm] = useState(false);
  const [editingReal, setEditingReal] = useState(null);
  const [realForm, setRealForm] = useState({ title: '', category: 'Mariage', location: '', guests: 0, description: '' });
  const [uploadingId, setUploadingId] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({ name: '', description: '', price: '', priceDay: '', caution: '', category: '', subcategory: '', image: '', status: 'Disponible', booqableUrl: '' });

  // ── Data fetching ──
  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/catalogue-requests`);
      if (response.ok) setContacts(await response.json());
    } catch (_) { /* network error */ }
    setLoading(false);
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API}/api/products`);
      if (response.ok) setProducts(await response.json());
    } catch (_) { /* network error */ }
  };

  const fetchRealizations = async () => {
    try {
      const response = await fetch(`${API}/api/realizations`);
      if (response.ok) setRealizations(await response.json());
    } catch (_) { /* network error */ }
  };

  // ── Auth ──
  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setError('');
      fetchContacts();
      fetchProducts();
      fetchRealizations();
    } else {
      setError('Mot de passe incorrect');
    }
  };

  // ── Contacts handlers ──
  const handleDeleteContact = async (id) => {
    if (window.confirm('Supprimer ce contact ?')) {
      await fetch(`${API}/api/catalogue-requests/${id}`, { method: 'DELETE' });
      fetchContacts();
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  // ── Products handlers ──
  const handleCreateProduct = async () => {
    try {
      const res = await fetch(`${API}/api/products`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productForm),
      });
      if (res.ok) {
        setShowProductForm(false);
        setProductForm({ name: '', description: '', price: '', priceDay: '', caution: '', category: '', subcategory: '', image: '', status: 'Disponible', booqableUrl: '' });
        fetchProducts();
      }
    } catch (_) { /* network error */ }
  };

  const handleUpdateProduct = async () => {
    try {
      const res = await fetch(`${API}/api/products/${editingProduct.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productForm),
      });
      if (res.ok) {
        setEditingProduct(null);
        setShowProductForm(false);
        setProductForm({ name: '', description: '', price: '', priceDay: '', caution: '', category: '', subcategory: '', image: '', status: 'Disponible', booqableUrl: '' });
        fetchProducts();
      }
    } catch (_) { /* network error */ }
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm('Supprimer ce produit ?')) {
      await fetch(`${API}/api/products/${id}`, { method: 'DELETE' });
      fetchProducts();
    }
  };

  const handleToggleStatus = async (product) => {
    const newStatus = product.status === 'Indisponible' ? 'Disponible' : 'Indisponible';
    await fetch(`${API}/api/products/${product.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    });
    fetchProducts();
  };

  // Déplace un produit dans l'ordre d'affichage de sa sous-catégorie
  const handleMoveProduct = async (product, direction) => {
    const scope = products.filter(
      (p) => p.category === product.category && p.subcategory === product.subcategory
    );
    const index = scope.findIndex((p) => p.id === product.id);
    const target = direction === 'top' ? 0 : direction === 'up' ? index - 1 : index + 1;
    if (index < 0 || target < 0 || target >= scope.length) return;

    const reordered = scope.slice();
    reordered.splice(index, 1);
    reordered.splice(target, 0, product);
    const ids = reordered.map((p) => p.id);

    // Mise à jour optimiste de l'affichage admin
    let cursor = 0;
    setProducts(products.map((p) => (ids.includes(p.id) ? reordered[cursor++] : p)));

    await fetch(`${API}/api/products/reorder`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    fetchProducts();
  };

  const startEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name || '', description: product.description || '',
      price: product.price || '', priceDay: product.priceDay || '',
      caution: product.caution || '', category: product.category || '',
      subcategory: product.subcategory || '', image: product.image || '',
      status: product.status || 'Disponible',
      booqableUrl: product.booqableUrl || '',
    });
    setShowProductForm(true);
  };

  // ── Realizations handlers ──
  const handleCreateRealization = async () => {
    const res = await fetch(`${API}/api/realizations`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(realForm),
    });
    if (res.ok) {
      setShowRealForm(false);
      setRealForm({ title: '', category: 'Mariage', location: '', guests: 0, description: '' });
      fetchRealizations();
    }
  };

  const handleUpdateRealization = async () => {
    const res = await fetch(`${API}/api/realizations/${editingReal.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(realForm),
    });
    if (res.ok) {
      setEditingReal(null);
      setRealForm({ title: '', category: 'Mariage', location: '', guests: 0, description: '' });
      fetchRealizations();
    }
  };

  const handleDeleteRealization = async (id) => {
    if (window.confirm('Supprimer cette réalisation ?')) {
      await fetch(`${API}/api/realizations/${id}`, { method: 'DELETE' });
      fetchRealizations();
    }
  };

  const handleUploadPhoto = async (realId, files) => {
    setUploadingId(realId);
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      await fetch(`${API}/api/realizations/${realId}/images`, { method: 'POST', body: formData });
    }
    setUploadingId(null);
    fetchRealizations();
  };

  const handleDeletePhoto = async (realId, imageUrl) => {
    await fetch(`${API}/api/realizations/${realId}/images?image_url=${encodeURIComponent(imageUrl)}`, { method: 'DELETE' });
    fetchRealizations();
  };

  const startEditRealization = (real) => {
    setEditingReal(real);
    setRealForm({ title: real.title, category: real.category, location: real.location, guests: real.guests, description: real.description });
  };

  // ── Login Screen ──
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 pt-32 pb-20">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-orange-500" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900" data-testid="admin-title">Administration</h1>
              <p className="text-gray-500 mt-1">Accès réservé</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Entrez le mot de passe" data-testid="admin-password-input" />
              </div>
              {error && <p className="text-red-500 text-sm" data-testid="admin-error">{error}</p>}
              <button type="submit" className="w-full py-3 bg-orange-500 text-white font-semibold rounded-xl hover:bg-orange-600 transition-colors" data-testid="admin-login-btn">
                Connexion
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ── Main Dashboard ──
  return (
    <div className="min-h-screen bg-gray-50 pt-28 pb-20">
      {/* Top Navigation Tabs - style 3dloc */}
      <div className="bg-white border-b shadow-sm mb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1" data-testid="admin-tabs">
              <button onClick={() => { setActiveTab('contacts'); fetchContacts(); }}
                className={`flex items-center gap-2 px-5 py-4 border-b-2 font-medium text-sm transition-colors ${activeTab === 'contacts' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                data-testid="tab-contacts">
                <Users className="w-4 h-4" />
                <span>Demandes de contact</span>
                <span className="ml-1 px-2 py-0.5 text-xs font-bold rounded-full bg-blue-100 text-blue-600">{contacts.length}</span>
              </button>
              <button onClick={() => setActiveTab('quotes')}
                className={`flex items-center gap-2 px-5 py-4 border-b-2 font-medium text-sm transition-colors ${activeTab === 'quotes' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                data-testid="tab-quotes">
                <FileSignature className="w-4 h-4" />
                <span>Demandes de devis</span>
              </button>
              <button onClick={() => { setActiveTab('products'); fetchProducts(); }}
                className={`flex items-center gap-2 px-5 py-4 border-b-2 font-medium text-sm transition-colors ${activeTab === 'products' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                data-testid="tab-products">
                <Package className="w-4 h-4" />
                <span>Produits</span>
                <span className={`ml-1 px-2 py-0.5 text-xs font-bold rounded-full ${activeTab === 'products' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600'}`}>{products.length}</span>
              </button>
              <button onClick={() => { setActiveTab('realizations'); fetchRealizations(); }}
                className={`flex items-center gap-2 px-5 py-4 border-b-2 font-medium text-sm transition-colors ${activeTab === 'realizations' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                data-testid="tab-realizations">
                <Camera className="w-4 h-4" />
                <span>Réalisations</span>
                <span className="ml-1 px-2 py-0.5 text-xs font-bold rounded-full bg-gray-100 text-gray-600">{realizations.length}</span>
              </button>
              <button onClick={() => setActiveTab('catalogue-pdf')}
                className={`flex items-center gap-2 px-5 py-4 border-b-2 font-medium text-sm transition-colors ${activeTab === 'catalogue-pdf' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                data-testid="tab-catalogue-pdf">
                <FileText className="w-4 h-4" />
                <span>Catalogue PDF</span>
              </button>
              <button onClick={() => setActiveTab('promo')}
                className={`flex items-center gap-2 px-5 py-4 border-b-2 font-medium text-sm transition-colors ${activeTab === 'promo' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                data-testid="tab-promo">
                <Megaphone className="w-4 h-4" />
                <span>Promotion</span>
              </button>
            </div>
            <button onClick={() => setIsAuthenticated(false)}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-500 hover:text-red-500 transition-colors" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {activeTab === 'contacts' && (
          <ContactsTab contacts={contacts} loading={loading} onRefresh={fetchContacts} onDelete={handleDeleteContact} formatDate={formatDate} onStatusChange={fetchContacts} />
        )}

        {activeTab === 'quotes' && <QuotesTab />}

        {activeTab === 'products' && (
          <ProductsTab
            products={products} searchQuery={searchQuery} setSearchQuery={setSearchQuery}
            categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter}
            showProductForm={showProductForm} setShowProductForm={setShowProductForm}
            editingProduct={editingProduct} setEditingProduct={setEditingProduct}
            productForm={productForm} setProductForm={setProductForm}
            onCreateProduct={handleCreateProduct} onUpdateProduct={handleUpdateProduct}
            onDeleteProduct={handleDeleteProduct} onToggleStatus={handleToggleStatus}
            onStartEdit={startEditProduct} onMoveProduct={handleMoveProduct}
          />
        )}

        {activeTab === 'realizations' && (
          <RealizationsTab
            realizations={realizations} showForm={showRealForm} setShowForm={setShowRealForm}
            editingReal={editingReal} setEditingReal={setEditingReal}
            realForm={realForm} setRealForm={setRealForm} uploadingId={uploadingId}
            onCreateRealization={handleCreateRealization} onUpdateRealization={handleUpdateRealization}
            onDeleteRealization={handleDeleteRealization} onUploadPhoto={handleUploadPhoto}
            onDeletePhoto={handleDeletePhoto} onStartEdit={startEditRealization}
          />
        )}

        {activeTab === 'catalogue-pdf' && <CataloguePdfTab />}

        {activeTab === 'promo' && <PromoTab />}
      </div>
    </div>
  );
};

export default AdminPage;
